import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { AuditRow } from "./types";

const PAGE_SIZE = 100;

const ChangeLogView = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<AuditRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async (before?: string) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
            if (before) params.set("before", before);
            const res = await apiFetch(`/access-control/audit?${params.toString()}`);
            if (!res.ok) {
                setError(t("server_error"));
                return;
            }
            const body: { rows: AuditRow[] } = await res.json();
            setRows(prev => (before ? [...prev, ...body.rows] : body.rows));
            setHasMore(body.rows.length >= PAGE_SIZE);
        } catch {
            setError(t("server_error"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const loadOlder = () => {
        const last = rows[rows.length - 1];
        if (last) load(last.changed_at);
    };

    return (
        <div className="ac-changelog">
            {error && <p className="ac-hint ac-hint--error">{error}</p>}
            <div className="ac-matrix-scroll">
                <table className="ac-matrix-table">
                    <thead>
                        <tr>
                            <th>{t("ac_audit_when")}</th>
                            <th>{t("ac_audit_who")}</th>
                            <th>{t("ac_audit_subject")}</th>
                            <th>{t("warehouse")}</th>
                            <th>{t("ac_key_col")}</th>
                            <th>{t("ac_audit_change")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.id}>
                                <td>{new Date(r.changed_at).toLocaleString()}</td>
                                <td>{r.changed_by}</td>
                                <td>{r.subject_type === "position" ? r.subject_id : `#${r.subject_id}`}</td>
                                <td>{r.warehouse ?? t("ac_defaults_tab")}</td>
                                <td><code className="ac-key-code">{r.perm_key}</code></td>
                                <td>{(r.old_level ?? "inherit")} → {r.new_level}</td>
                            </tr>
                        ))}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={6} className="ac-hint">{t("ac_audit_empty")}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {loading && <div className="ac-users-loading">{t("loading")}</div>}
            {!loading && hasMore && rows.length > 0 && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={loadOlder}>
                    {t("ac_audit_load_older")}
                </button>
            )}
        </div>
    );
};

export default ChangeLogView;
